import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  isMessageKey,
  DEFAULT_LANGUAGE,
  LANGUAGE_DIRECTION,
  formatContextFor,
  translate,
  type Language,
  type MessageKey,
  type TranslateOptions,
} from '@whinerp/contracts/i18n';
import { type FormatContext } from '@whinerp/contracts/format';

/**
 * Language, direction and formatting.
 *
 * Arabic is a toggle, off by default, and turning it on mirrors the layout
 * rather than right-aligning Latin text: `dir` is set on the document, and
 * every component uses logical properties (start/end, ps/pe) so the mirroring
 * is structural rather than a second stylesheet.
 *
 * Numerals, timezone and currency are separate from language on purpose. An
 * Arabic interface with Latin digits and AED is the common Gulf preference,
 * and collapsing the four into one setting takes that choice away.
 */

export interface I18nContextValue {
  language: Language;
  direction: 'ltr' | 'rtl';
  /** Translate. Throws if a placeholder has nothing to fill it. */
  t: (key: MessageKey, options?: TranslateOptions) => string;
  format: FormatContext;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  language?: Language;
  locale?: string;
  timeZone?: string;
  currency?: string;
  numerals?: 'latn' | 'arab';
  secondaryCalendar?: 'islamic-umalqura' | 'none';
  children: ReactNode;
}

export function I18nProvider({
  language,
  locale,
  timeZone,
  currency,
  numerals,
  secondaryCalendar,
  children,
}: I18nProviderProps): JSX.Element {
  const value = useMemo<I18nContextValue>(() => {
    const resolved = language ?? DEFAULT_LANGUAGE;
    return {
      language: resolved,
      direction: LANGUAGE_DIRECTION[resolved],
      t: (key, options) => translate(resolved, key, options),
      // Built by the contracts helper rather than here, so a test can build
      // the same context and assert what a screen will actually render.
      format: formatContextFor(resolved, {
        ...(locale ? { locale } : {}),
        ...(timeZone ? { timeZone } : {}),
        ...(currency ? { currency } : {}),
        ...(numerals ? { numerals } : {}),
        ...(secondaryCalendar ? { secondaryCalendar } : {}),
      }),
    };
  }, [language, locale, timeZone, currency, numerals, secondaryCalendar]);

  return (
    <I18nContext.Provider value={value}>
      <DocumentLanguage language={value.language} direction={value.direction} />
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Sets lang and dir on the document element.
 *
 * On the element rather than a wrapper div, because the browser's own text
 * shaping, selection and scrollbar placement all read it from there - a
 * mirrored div inside a left-to-right document is the "right-aligned Latin"
 * failure this exists to avoid.
 */
function DocumentLanguage({ language, direction }: { language: Language; direction: 'ltr' | 'rtl' }): null {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
  }
  return null;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n was called outside an I18nProvider. Providers wraps every screen and every test.');
  }
  return context;
}

/** The translate function alone, which is what most call sites want. */
export function useT(): (key: MessageKey, options?: TranslateOptions) => string {
  return useI18n().t;
}

/** The format context alone, for the one formatter. */
export function useFormat(): FormatContext {
  return useI18n().format;
}

/**
 * The sentence an error should show this reader.
 *
 * Prefers the server's catalogue key over its English. Every message this
 * server composes is English in every locale - the actor's language is not on
 * the request context and no server code calls `translate` - so the sentence
 * travels as a key and the client, which already holds the catalogue and the
 * locale, renders it.
 *
 * Falls back to the English rather than to the key itself. A screen showing
 * `permissions.lastAdmin` to a person is worse than one showing an English
 * sentence they can at least act on, and this is a conversion in progress
 * rather than a finished one.
 */
export function useErrorMessage(): (error: {
  message: string;
  messageKey?: string;
  messageValues?: Record<string, string>;
}) => string {
  const t = useT();
  return (error) => {
    if (error.messageKey !== undefined && isMessageKey(error.messageKey)) {
      return t(error.messageKey, error.messageValues ? { values: error.messageValues } : undefined);
    }
    return error.message;
  };
}
